import { jsx as _jsx } from "react/jsx-runtime";
const BG = ["#EDE9FE", "#DBEAFE", "#DCFCE7", "#FEF3C7", "#FFE4E6", "#E0F2FE", "#F3E8FF", "#ECFCCB", "#FFEDD5", "#E2E8F0", "#CCFBF1", "#FCE7F3"];
const SKIN = [["#FBDCC6", "#EFC3A6"], ["#F3C99D", "#E2B083"], ["#E2AD72", "#CC955B"], ["#C68642", "#AD7136"], ["#8D5524", "#764619"], ["#5E3A22", "#4C2E1A"]];
const HAIR = ["#231A14", "#4A3222", "#7B4B2A", "#B9793A", "#E3C07A", "#A63D2A", "#9CA3AF", "#EDEDED", "#6D28D9", "#2563EB", "#DB2777", "#0F766E"];
const SHIRT = ["#6D28D9", "#2563EB", "#059669", "#F59E0B", "#E11D48", "#0EA5E9", "#1F2937", "#14B8A6", "#F97316", "#8B5CF6", "#65A30D", "#DB2777"];
const INK = "#1E1B2E";
const SHORT = ["crop", "swoop", "quiff", "spiky", "buzz", "mohawk", "bald"];
const LONGISH = ["long", "bob", "afro", "bun", "pony", "curls"];
const HATS = ["beanie", "cap", "scarf"];
/** A small seeded random number generator, so the gallery is the same everywhere, every time. */
function rng(seed) {
    return () => { seed |= 0; seed = (seed + 0x6d2b79f5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
function buildRecipes() {
    const r = rng(20261012);
    const pick = (a) => a[Math.floor(r() * a.length)];
    const out = [];
    const seen = new Set();
    let i = 0;
    while (out.length < 100) {
        i++;
        // An even spread of hair families, skin tones and backdrops, rather than leaving it all to chance.
        const fam = out.length % 10;
        const hair = fam < 4 ? pick(SHORT) : fam < 9 ? pick(LONGISH) : pick(HATS);
        const skin = (out.length * 7 + Math.floor(r() * 3)) % SKIN.length;
        let hc = pick([0, 0, 1, 1, 2, 2, 3, 4, 5, 0, 1, 6, 7, 8, 9, 10, 11].slice(0, r() < 0.12 ? 17 : 13));
        if (hair === "bald" || hair === "scarf")
            hc = 0;
        const rec = {
            bg: out.length % BG.length, skin, hair, hc,
            shirt: Math.floor(r() * SHIRT.length), top: pick(["crew", "crew", "vee", "hood", "collar"]),
            eyes: pick(["dot", "dot", "dot", "happy", "wink"]), mouth: pick(["smile", "smile", "grin", "flat"]),
            blush: r() < 0.35,
        };
        if (hair === "scarf" && rec.top === "vee")
            rec.top = "crew";
        if (r() < 0.24)
            rec.glasses = pick(["round", "square"]);
        if (SHORT.includes(hair) && r() < 0.42)
            rec.beard = pick(["full", "tache", "stubble", "full"]);
        if (LONGISH.includes(hair) && r() < 0.35)
            rec.ear = true;
        if (hair !== "scarf" && hair !== "cap" && hair !== "beanie" && r() < 0.06)
            rec.phones = true;
        const key = `${rec.hair}|${rec.hc}|${rec.skin}|${rec.glasses ?? ""}|${rec.beard ?? ""}|${rec.shirt}`;
        if (seen.has(key) && i < 5000)
            continue;
        seen.add(key);
        out.push(rec);
    }
    return out;
}
let RECIPES = null;
const recipes = () => (RECIPES ??= buildRecipes());
export const AVATAR_IDS = Array.from({ length: 100 }, (_, n) => `av${String(n + 1).padStart(3, "0")}`);
const circles = (pts, fill) => pts.map(([x, y, r]) => `<circle cx="${x}" cy="${y}" r="${r}" fill="${fill}"/>`).join("");
/** The SVG for one character. */
export function avatarSvg(id) {
    const n = Number(id.replace(/^av/, "")) - 1;
    const c = recipes()[n] ?? recipes()[0];
    const [skin, shade] = SKIN[c.skin];
    const hair = HAIR[c.hc];
    const shirt = SHIRT[c.shirt];
    const back = [];
    const front = [];
    // Hair that sits behind the head.
    switch (c.hair) {
        case "long":
            back.push(`<path d="M29 45C27 26 37 17 48 17s21 9 19 28l2 25c-6 4-13 3-17 0h-8c-4 3-11 4-17 0z" fill="${hair}"/>`);
            break;
        case "bob":
            back.push(`<path d="M29.5 47C28 26 38 18 48 18s20 8 18.5 29l.5 11c-5 2-9 1-11-2H40c-2 3-6 4-11 2z" fill="${hair}"/>`);
            break;
        case "afro":
            back.push(circles([[48, 33, 21], [30, 30, 9], [66, 30, 9], [34, 18, 9], [62, 18, 9], [48, 13, 10], [28, 42, 7], [68, 42, 7]], hair));
            break;
        case "curls":
            back.push(circles([[33, 30, 9], [63, 30, 9], [40, 21, 9], [56, 21, 9], [48, 18, 9], [29, 42, 8], [67, 42, 8], [29, 54, 8], [67, 54, 8], [31, 64, 7], [65, 64, 7]], hair));
            break;
        case "bun":
            back.push(`<circle cx="48" cy="15" r="8.5" fill="${hair}"/>`);
            break;
        case "pony":
            back.push(`<path d="M60 30c10 1 14 10 12 21-1 7-5 12-9 14 2-8 1-16-5-22z" fill="${hair}"/>`);
            break;
        case "scarf":
            back.push(`<path d="M26.5 46C25.5 25 36 16 48 16s22.5 9 21.5 30c0 13-4 21-10 27H36c-6-6-9.5-14-9.5-27z" fill="${shirt}"/>`);
            break;
    }
    // Hair, hats and the rest that sit on top.
    switch (c.hair) {
        case "crop":
            front.push(`<path d="M31.5 42C30 27 38 20 48 20s18 7 16.5 22c-1.5-7-4.5-11-8.5-12-5 3-14 3-21 1-2 3-3 7-3.5 11z" fill="${hair}"/>`);
            break;
        case "swoop":
            front.push(`<path d="M31.5 41C29 26 38 19 49 19s18 8 15.5 22c-1.5-7-3.5-10-6.5-12-8 2-18 0-23 4-2 2-3 5-3.5 8z" fill="${hair}"/>`);
            break;
        case "quiff":
            front.push(`<path d="M32 40c-2-12 4-18 10-19 2-6 14-7 18-1 5 4 6 12 4.5 20-2.5-7-6.5-10-12.5-10-8 1-15 3-20 10z" fill="${hair}"/>`);
            break;
        case "spiky":
            front.push(`<path d="M31.5 40 30 30l5 1-1-8 6 3 2-8 5 6 4-7 3 7 6-4v7l6-1-1.5 14c-2.5-7-7.5-10-16.5-10s-14 3-16.5 10z" fill="${hair}"/>`);
            break;
        case "buzz":
            front.push(`<path d="M31.8 38c.2-12 7.2-16.5 16.2-16.5S64 26 64.2 38c-4.2-7-9.2-9-16.2-9s-12 2-16.2 9z" fill="${hair}" opacity=".7"/>`);
            break;
        case "mohawk":
            front.push(`<path d="M31.8 38c.2-12 7.2-16.5 16.2-16.5S64 26 64.2 38c-4.2-7-9.2-9-16.2-9s-12 2-16.2 9z" fill="${hair}" opacity=".45"/><path d="M41 30c-1-8 3-14 7-15 5 0 8 6 7 15-4-2-10-2-14 0z" fill="${hair}"/>`);
            break;
        case "long":
        case "bob":
            front.push(`<path d="M31 44c-2-17 7-25 17-25 11 0 19 8 17 25-2-8-5-13-11-15-4 4-13 6-20 6-1.5 3-2.4 6-3 9z" fill="${hair}"/>`);
            break;
        case "afro":
            front.push(`<path d="M31.5 40c-.5-10 6.5-15 16.5-15s17 5 16.5 15c-4-7-9-9-16.5-9s-12.5 2-16.5 9z" fill="${hair}"/>`);
            break;
        case "curls":
            front.push(circles([[38, 27, 6], [46, 24, 6.5], [54, 25, 6], [60, 30, 5], [34, 33, 5]], hair));
            break;
        case "bun":
        case "pony":
            front.push(`<path d="M31.5 41C30 27 38 20 48 20s18 7 16.5 21c-3-6-7-10-13-11-6 2-14 3-19 7-.5 1.5-.8 3-1 4z" fill="${hair}"/>`);
            break;
        case "beanie":
            front.push(`<path d="M30 39c0-14 8-21 18-21s18 7 18 21z" fill="${shirt}"/><rect x="29" y="34" width="38" height="7.5" rx="3.75" fill="${shirt}"/><rect x="29" y="34" width="38" height="7.5" rx="3.75" fill="#fff" opacity=".22"/><circle cx="48" cy="16" r="4.5" fill="${shirt}"/>`);
            break;
        case "cap":
            front.push(`<path d="M31 37c0-12 8-17 17-17s17 5 17 17z" fill="${shirt}"/><path d="M45 35c9-2.5 21-1.5 29 2.5-6 2.5-18 2-29 .5z" fill="${shirt}"/><path d="M45 35c9-2.5 21-1.5 29 2.5-6 2.5-18 2-29 .5z" fill="#000" opacity=".18"/><circle cx="48" cy="20" r="1.8" fill="#fff" opacity=".5"/>`);
            break;
        case "scarf":
            front.push(`<path d="M31 38c0-12 8-18 17-18s17 6 17 18c-4-7-10-10-17-10s-13 3-17 10z" fill="${shirt}"/><path d="M31 38c0-12 8-18 17-18s17 6 17 18c-4-7-10-10-17-10s-13 3-17 10z" fill="#fff" opacity=".12"/>`);
            break;
    }
    // Shoulders and top.
    const body = [`<path d="M12 96c0-20 15-29 36-29s36 9 36 29z" fill="${shirt}"/>`];
    if (c.top === "vee")
        body.push(`<path d="M41 67.5 48 78l7-10.5c-2-.3-4.5-.5-7-.5s-5 .2-7 .5z" fill="${shade}"/>`);
    if (c.top === "crew")
        body.push(`<path d="M38 68.5c3 3.5 6.5 5 10 5s7-1.5 10-5" fill="none" stroke="#fff" stroke-opacity=".35" stroke-width="2.4" stroke-linecap="round"/>`);
    if (c.top === "collar")
        body.push(`<path d="M39 67.5 48 75l-5 6zM57 67.5 48 75l5 6z" fill="#fff"/>`);
    if (c.top === "hood")
        body.push(`<path d="M30 76c4-8 10-11 18-11s14 3 18 11c-5-4-11-6-18-6s-13 2-18 6z" fill="#000" opacity=".16"/><path d="M44 74v9M52 74v9" stroke="#fff" stroke-opacity=".7" stroke-width="1.6" stroke-linecap="round"/>`);
    // Face.
    const face = [];
    const ex = [41, 55];
    if (c.eyes === "dot")
        face.push(ex.map((x) => `<ellipse cx="${x}" cy="44" rx="2.1" ry="2.5" fill="${INK}"/>`).join(""));
    if (c.eyes === "happy")
        face.push(ex.map((x) => `<path d="M${x - 2.6} 45c1.4-2.2 3.8-2.2 5.2 0" fill="none" stroke="${INK}" stroke-width="2" stroke-linecap="round"/>`).join(""));
    if (c.eyes === "wink")
        face.push(`<ellipse cx="41" cy="44" rx="2.1" ry="2.5" fill="${INK}"/><path d="M52.4 45c1.4-2.2 3.8-2.2 5.2 0" fill="none" stroke="${INK}" stroke-width="2" stroke-linecap="round"/>`);
    if (c.blush)
        face.push(`<circle cx="37" cy="50" r="3" fill="#F43F5E" opacity=".16"/><circle cx="59" cy="50" r="3" fill="#F43F5E" opacity=".16"/>`);
    const beardFill = c.hair === "bald" || c.hair === "scarf" || c.hair === "beanie" || c.hair === "cap" ? HAIR[1] : hair;
    if (c.beard === "full")
        face.push(`<path d="M31.8 45c.8 12 7.5 18.5 16.2 18.5S63.4 57 64.2 45c-1.8 5.5-5.2 8.5-9.2 8.5-2.5-1.5-11.5-1.5-14 0-4 0-7.4-3-9.2-8.5z" fill="${beardFill}"/>`);
    if (c.beard === "stubble")
        face.push(`<path d="M32 46c1 11 7.5 17 16 17s15-6 16-17c-2 6-5.5 9-9 9-2.5-1.3-11.5-1.3-14 0-3.5 0-7-3-9-9z" fill="${beardFill}" opacity=".28"/>`);
    if (c.beard === "tache")
        face.push(`<path d="M41.5 51.6c2.6-2.2 5.2-1.8 6.5-.5 1.3-1.3 3.9-1.7 6.5.5-2.6 1.6-5 1.2-6.5.4-1.5.8-3.9 1.2-6.5-.4z" fill="${beardFill}"/>`);
    if (c.mouth === "smile")
        face.push(`<path d="M43.5 53.5c2.6 2.4 6.4 2.4 9 0" fill="none" stroke="${INK}" stroke-width="2" stroke-linecap="round"/>`);
    if (c.mouth === "grin")
        face.push(`<path d="M43 52.5h10c0 3.4-2.3 5.5-5 5.5s-5-2.1-5-5.5z" fill="${INK}"/><path d="M44.5 52.5h7v1.4c-2.3.6-4.7.6-7 0z" fill="#fff"/>`);
    if (c.mouth === "flat")
        face.push(`<path d="M44.5 54.2h7" stroke="${INK}" stroke-width="2" stroke-linecap="round"/>`);
    if (c.glasses === "round")
        face.push(`<g fill="#fff" fill-opacity=".18" stroke="${INK}" stroke-width="1.7"><circle cx="41" cy="44" r="5.4"/><circle cx="55" cy="44" r="5.4"/></g><path d="M46.4 43.6c1-.8 2.2-.8 3.2 0" fill="none" stroke="${INK}" stroke-width="1.7"/>`);
    if (c.glasses === "square")
        face.push(`<g fill="#fff" fill-opacity=".18" stroke="${INK}" stroke-width="1.7"><rect x="35" y="39.8" width="11.4" height="8.6" rx="2.6"/><rect x="49.6" y="39.8" width="11.4" height="8.6" rx="2.6"/></g><path d="M46.4 43.6c1-.8 2.2-.8 3.2 0" fill="none" stroke="${INK}" stroke-width="1.7"/>`);
    if (c.ear)
        face.push(`<circle cx="31.6" cy="50.5" r="1.7" fill="#FBBF24"/><circle cx="64.4" cy="50.5" r="1.7" fill="#FBBF24"/>`);
    const ears = c.hair === "scarf" ? "" : `<circle cx="31.6" cy="45" r="4" fill="${skin}"/><circle cx="64.4" cy="45" r="4" fill="${skin}"/>`;
    const neck = c.hair === "scarf" ? "" : `<rect x="41.5" y="54" width="13" height="16" rx="6" fill="${shade}"/>`;
    const phones = c.phones ? `<path d="M29.5 44c0-17 8-24 18.5-24s18.5 7 18.5 24" fill="none" stroke="${INK}" stroke-width="3.4"/><rect x="25" y="39" width="8" height="13" rx="4" fill="${SHIRT[(c.shirt + 3) % SHIRT.length]}"/><rect x="63" y="39" width="8" height="13" rx="4" fill="${SHIRT[(c.shirt + 3) % SHIRT.length]}"/>` : "";
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96"><rect width="96" height="96" fill="${BG[c.bg]}"/><circle cx="76" cy="18" r="22" fill="#fff" opacity=".35"/><g transform="translate(48 62) scale(1.14) translate(-48 -62)">${back.join("")}${body.join("")}${neck}${ears}<ellipse cx="48" cy="43" rx="16.5" ry="19" fill="${skin}"/>${face.join("")}<path d="M46.6 48.6c.9.8 1.9.8 2.8 0" fill="none" stroke="${shade}" stroke-width="1.6" stroke-linecap="round"/>${front.join("")}${phones}</g></svg>`;
}
const cache = new Map();
/** An <img src> for any avatar value: a preset character, an uploaded photo, or null for none. */
export function avatarSrc(avatar) {
    if (!avatar)
        return null;
    if (!avatar.startsWith("preset:"))
        return avatar;
    const id = avatar.slice(7);
    if (!AVATAR_IDS.includes(id))
        return null;
    let s = cache.get(id);
    if (!s) {
        s = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(avatarSvg(id))}`;
        cache.set(id, s);
    }
    return s;
}
const initialsOf = (name) => name.replace(/\(.*?\)/g, "").trim().split(/\s+/).filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
/** A round avatar: their character or photo, or their initials. */
export function Avatar({ avatar, name, size = 36, className }) {
    const src = avatarSrc(avatar);
    return (_jsx("span", { className: `tc-av${className ? ` ${className}` : ""}`, style: { width: size, height: size, fontSize: Math.round(size * 0.36) }, "aria-hidden": "true", children: src ? _jsx("img", { src: src, alt: "" }) : initialsOf(name) }));
}
